import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { LoadScript, GoogleMap } from '@react-google-maps/api';
import {  getActiveDrivers } from '../../shared/api/user';
import { CITY_CENTERS } from '../../shared/constants/cities';

const RidersMap = () => {
  const [drivers, setDrivers] = useState([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [selectedCity, setSelectedCity] = useState("lahore");
  const center = CITY_CENTERS[selectedCity];
  const [selectedDriverId, setSelectedDriverId] = useState("all");

  const VEHICLE_IMAGE = "/images/vehicle.png"; 

  const isValidPakistanCoordinate = (lat, lng) => {
  // Pakistan's approximate geographical bounds
  const PAKISTAN_BOUNDS = {
    minLat: 23.5, maxLat: 37.0,
    minLng: 60.5, maxLng: 77.0
  };
  
  return (
    lat >= PAKISTAN_BOUNDS.minLat &&
    lat <= PAKISTAN_BOUNDS.maxLat &&
    lng >= PAKISTAN_BOUNDS.minLng &&
    lng <= PAKISTAN_BOUNDS.maxLng
  );
};

const normalizeCoordinates = (coords) => {
  if (!coords || coords.length !== 2) return null;
  
  let [first, second] = coords.map(Number);
  
  // If both are valid Pakistan coordinates, return as-is
  if (isValidPakistanCoordinate(first, second)) {
    return { lat: first, lng: second };
  }
  
  // If reversed coordinates would be valid, return swapped
  if (isValidPakistanCoordinate(second, first)) {
    return { lat: second, lng: first };
  }
  
  return null;
};


  const mapOptions = {
    zoom: 12,
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'AIzaSyABZz69mTPSrpIf_GmmJQ6Oyhvj0R8DCGU'

  };

  // Fetch new API data
  const fetchDrivers = async () => {
    setLoading(true);
    try {      
      const data = await getActiveDrivers("68751ed3593d1fdb619692da");
      if (data.success && Array.isArray(data.drivers)) {
              const cityDrivers = data.drivers.filter(
        d => d.isOnline && d.city?.toLowerCase() === selectedCity
      );
      setDrivers(cityDrivers);
      setSelectedDriverId("all"); // reset on city change
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("Failed to fetch drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  fetchDrivers();
}, [selectedCity]);


  useEffect(() => {
    return () => {
      markersRef.current.forEach(marker => {
        if (marker.map) marker.map = null;
      });
      markersRef.current = [];
    };
  }, []);

useEffect(() => {
    const addMarkers = async () => {
      if (!isMapLoaded || !window.google?.maps?.importLibrary || !drivers.length) return;

      try {
        const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");
        const { InfoWindow } = await window.google.maps.importLibrary("maps");

        // Clear previous markers
        markersRef.current.forEach(marker => marker.map = null);
        markersRef.current = [];

        infoWindowRef.current = new InfoWindow();

        const filteredDrivers = drivers.filter(
          (d) => selectedDriverId === "all" || d._id === selectedDriverId
        );

        filteredDrivers.forEach((driver) => {
          const coords = driver.currentLocation?.coordinates;
          const normalized = normalizeCoordinates(coords);
          
          if (!normalized) {
            console.warn('Invalid coordinates for driver:', driver._id, coords);
            return;
          }

          const { lat, lng } = normalized;

          const imgElement = document.createElement("img");
          imgElement.src = VEHICLE_IMAGE;
          imgElement.alt = "vehicle";
          imgElement.style.width = "50px";
          imgElement.style.height = "50px";
          imgElement.style.objectFit = "contain";

          const marker = new AdvancedMarkerElement({
            map: mapRef.current,
            position: { lat, lng },
            title: `${driver.firstName} ${driver.lastName} ${driver.phoneNumber}`,
            content: imgElement
          });

          marker.addListener("mouseover", () => {
            infoWindowRef.current.setContent(`
              <div style="padding: 8px; background: white; border-radius: 4px; box-shadow: 0 2px 7px rgba(0,0,0,0.3);">
                <img 
                  src="${VEHICLE_IMAGE}" 
                  alt="vehicle"
                  style="width: 60px; height: 40px; object-fit: contain; float: left; margin-right: 12px;"
                />
                <div>
                  <strong style="color: #1976D2;">${driver.firstName} ${driver.lastName}</strong>
                  ${driver.vehicle?.plateNumber ? `<div style="margin-top: 4px;">${driver.vehicle.plateNumber}</div>` : ''}
                  ${driver.phoneNumber ? `<div style="margin-top: 4px;">${driver.phoneNumber}</div>` : ''}
                </div>
                <div style="clear: both;"></div>
              </div>
            `);
            infoWindowRef.current.open({
              anchor: marker,
              map: mapRef.current,
            });
          });

          marker.addListener("mouseout", () => {
            infoWindowRef.current.close();
          });

          markersRef.current.push(marker);
        });
      } catch (error) {
        console.error("Error adding markers:", error);
      }
    };

    addMarkers();
  }, [drivers, isMapLoaded, selectedDriverId]);


  return (
    <>
    
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
  {/* City Dropdown */}
  <Box>
    <label style={{ fontWeight: 'bold', marginRight: '6px' }}>City:</label>
    <select
      value={selectedCity}
      onChange={(e) => setSelectedCity(e.target.value)}
      style={{ padding: '6px 12px', fontSize: '16px' }}
    >
      {Object.keys(CITY_CENTERS).map((city) => (
        <option key={city} value={city}>
          {city.charAt(0).toUpperCase() + city.slice(1)}
        </option>
      ))}
    </select>
  </Box>

  {/* Driver Dropdown */}
  <Box>
    <label style={{ fontWeight: 'bold', marginRight: '6px' }}>Rider:</label>
    <select
      value={selectedDriverId}
      onChange={(e) => setSelectedDriverId(e.target.value)}
      style={{ padding: '6px 12px', fontSize: '16px', minWidth: '200px' }}
    >
      <option value="all">All Riders</option>
      {drivers.map((driver) => (
        <option key={driver._id} value={driver._id}>
          {driver.firstName} {driver.lastName}
        </option>
      ))}
    </select>
  </Box>

  {/* Refresh Button */}
  <button
    onClick={fetchDrivers}
    style={{
      padding: '6px 16px',
      fontSize: '16px',
      backgroundColor: '#1976D2',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer'
    }}
  >
    Refresh
  </button>
</Box>

    <Box sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Riders Map - Total Online Riders: {drivers.length}
      </Typography>
      {loading ? (
        <Typography>Loading map...</Typography>
      ) : (
        <Box sx={{ height: '800px', width: '100%' }}>
          <LoadScript googleMapsApiKey={'AIzaSyABZz69mTPSrpIf_GmmJQ6Oyhvj0R8DCGU'}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '800px' }}
              center={center}
              zoom={12}
              options={mapOptions}
              onLoad={(map) => {
                mapRef.current = map;
                setIsMapLoaded(true);
              }}
              onUnmount={() => {
                setIsMapLoaded(false);
              }}
            />
          </LoadScript>
        </Box>
      )}
    </Box>
    </>
  );
};

export default RidersMap;
